import { useSuspenseQuery, useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { userDataService } from './user-data-service';
import { UserData, OrgUserData, OrgUserDataKey, OrgUserDataValue, LastViewedVideo, getOrgUserData } from './user-data-interfaces';
import { useMemo } from 'react';

const USER_DATA_QUERY_KEY = ['userData'] as const;
const LAST_SELECTED_ORG_KEY = ['lastSelectedOrg'] as const;

// Core user data hooks
export const useUserData = () => {
    return useSuspenseQuery({
        queryKey: USER_DATA_QUERY_KEY,
        queryFn: userDataService.getUserData,
        staleTime: 5 * 60 * 1000, // 5 minutes
        gcTime: 10 * 60 * 1000, // 10 minutes
    });
};

export const useOrgUserData = (orgId: string) => {
    const { data: userData } = useUserData();
    return useMemo(() => getOrgUserData(userData, orgId), [userData, orgId]);
};

export const useUpdateOrgUserData = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async (params: { orgId: string; key: OrgUserDataKey; value: OrgUserDataValue<OrgUserDataKey> }) => {
            return userDataService.updateOrgUserData(params.orgId, params.key, params.value);
        },
        onSuccess: (updatedOrgData, variables) => {
            // Update the userData cache
            queryClient.setQueryData(USER_DATA_QUERY_KEY, (oldData: UserData | undefined) => {
                if (!oldData) return oldData;
                return { ...oldData, [variables.orgId]: updatedOrgData };
            });
        },
        onError: (error) => {
            console.error('Failed to update org user data:', error);
        },
    });
};

// Bookmark hooks (updated to use org-specific system)
export const useBookmarks = (orgId: string) => {
    const orgData = useOrgUserData(orgId);
    return orgData.bookmarks;
};

export const useIsBookmarked = (orgId: string, videoId: string) => {
    const bookmarks = useBookmarks(orgId);
    return userDataService.isBookmarked(videoId, bookmarks);
};

export const useToggleBookmark = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: async ({ orgId, videoId, isCurrentlyBookmarked }: {
            orgId: string;
            videoId: string;
            isCurrentlyBookmarked: boolean;
        }) => {
            if (isCurrentlyBookmarked) {
                return userDataService.removeBookmark(orgId, videoId);
            } else {
                return userDataService.addBookmark(orgId, videoId);
            }
        },
        onSuccess: (updatedBookmarks, variables) => {
            // Update the userData cache
            queryClient.setQueryData(USER_DATA_QUERY_KEY, (oldData: UserData | undefined) => {
                if (!oldData) return oldData;
                const currentOrgData = getOrgUserData(oldData, variables.orgId);
                return {
                    ...oldData,
                    [variables.orgId]: {
                        ...currentOrgData,
                        bookmarks: updatedBookmarks
                    }
                };
            });
        },
        onError: (error) => {
            console.error('Failed to toggle bookmark:', error);
        },
    });
};

export const useVideoBookmark = (orgId: string, videoId: string) => {
    const isBookmarked = useIsBookmarked(orgId, videoId);
    const toggleBookmark = useToggleBookmark();

    const handleToggle = () => {
        toggleBookmark.mutate({
            orgId,
            videoId,
            isCurrentlyBookmarked: isBookmarked,
        });
    };

    return {
        isBookmarked,
        toggleBookmark: handleToggle,
        isLoading: toggleBookmark.isPending,
        error: toggleBookmark.error,
    };
};

// Last viewed hooks
export const useLastViewed = (orgId: string) => {
    const orgData = useOrgUserData(orgId);
    return orgData.lastViewed;
};

export const useAddLastViewed = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ orgId, videoId }: { orgId: string; videoId: string }) => {
            return userDataService.addLastViewed(orgId, videoId);
        },
        onSuccess: (updatedLastViewed, variables) => {
            queryClient.setQueryData(USER_DATA_QUERY_KEY, (oldData: UserData | undefined) => {
                if (!oldData) return oldData;
                const currentOrgData = getOrgUserData(oldData, variables.orgId);
                return {
                    ...oldData,
                    [variables.orgId]: {
                        ...currentOrgData,
                        lastViewed: updatedLastViewed
                    }
                };
            });
        },
        onError: (error) => {
            console.error('Failed to add last viewed:', error);
        },
    });
};

export const useRemoveLastViewed = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ orgId, videoId }: { orgId: string; videoId: string }) => {
            return userDataService.removeLastViewed(orgId, videoId);
        },
        onSuccess: (updatedLastViewed, variables) => {
            queryClient.setQueryData(USER_DATA_QUERY_KEY, (oldData: UserData | undefined) => {
                if (!oldData) return oldData;
                const currentOrgData = getOrgUserData(oldData, variables.orgId);
                return {
                    ...oldData,
                    [variables.orgId]: {
                        ...currentOrgData,
                        lastViewed: updatedLastViewed
                    }
                };
            });
        },
        onError: (error) => {
            console.error('Failed to remove last viewed:', error);
        },
    });
};

export const useClearLastViewed = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ orgId }: { orgId: string }) => {
            return userDataService.clearLastViewed(orgId);
        },
        onSuccess: (updatedLastViewed, variables) => {
            queryClient.setQueryData(USER_DATA_QUERY_KEY, (oldData: UserData | undefined) => {
                if (!oldData) return oldData;
                const currentOrgData = getOrgUserData(oldData, variables.orgId);
                return {
                    ...oldData,
                    [variables.orgId]: {
                        ...currentOrgData,
                        lastViewed: updatedLastViewed
                    }
                };
            });
        },
        onError: (error) => {
            console.error('Failed to clear last viewed:', error);
        },
    });
};

export const useVideoLastViewed = (orgId: string, videoId: string) => {
    const lastViewed = useLastViewed(orgId);
    return useMemo(() => {
        return lastViewed.find(v => v.videoId === videoId);
    }, [lastViewed, videoId]);
};

// Preferences hooks
export const useUserPreferences = (orgId: string) => {
    const orgData = useOrgUserData(orgId);
    return orgData.preferences;
};

export const useUpdatePreferences = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ orgId, preferences }: { orgId: string; preferences: Partial<OrgUserData['preferences']> }) => {
            return userDataService.updatePreferences(orgId, preferences);
        },
        onSuccess: (updatedPreferences, variables) => {
            queryClient.setQueryData(USER_DATA_QUERY_KEY, (oldData: UserData | undefined) => {
                if (!oldData) return oldData;
                const currentOrgData = getOrgUserData(oldData, variables.orgId);
                return {
                    ...oldData,
                    [variables.orgId]: {
                        ...currentOrgData,
                        preferences: updatedPreferences
                    }
                };
            });
        },
        onError: (error) => {
            console.error('Failed to update preferences:', error);
        },
    });
};

// Last selected organization hooks
export const useLastSelectedOrg = () => {
    return useQuery({
        queryKey: LAST_SELECTED_ORG_KEY,
        queryFn: userDataService.getLastSelectedOrgId,
        staleTime: Infinity,
    });
};

export const useSetLastSelectedOrg = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: (orgId: string) => userDataService.setLastSelectedOrgId(orgId),
        onSuccess: (_data, orgId) => {
            queryClient.setQueryData(LAST_SELECTED_ORG_KEY, orgId);
        },
        onError: (error) => {
            console.error('Failed to set last selected org:', error);
        },
    });
};

export const useClearLastSelectedOrg = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: userDataService.clearLastSelectedOrgId,
        onSuccess: () => {
            queryClient.setQueryData(LAST_SELECTED_ORG_KEY, null);
        },
        onError: (error) => {
            console.error('Failed to clear last selected org:', error);
        },
    });
};

// Utility hooks
export const useResetUserData = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: userDataService.resetUserData,
        onSuccess: (resetData) => {
            queryClient.setQueryData(USER_DATA_QUERY_KEY, resetData);
            queryClient.setQueryData(LAST_SELECTED_ORG_KEY, null);
        },
        onError: (error) => {
            console.error('Failed to reset user data:', error);
        },
    });
};

export const useResetOrgUserData = () => {
    const queryClient = useQueryClient();

    return useMutation({
        mutationFn: ({ orgId }: { orgId: string }) => {
            return userDataService.resetOrgUserData(orgId);
        },
        onSuccess: (resetOrgData, variables) => {
            queryClient.setQueryData(USER_DATA_QUERY_KEY, (oldData: UserData | undefined) => {
                if (!oldData) return oldData;
                return { ...oldData, [variables.orgId]: resetOrgData };
            });
        },
        onError: (error) => {
            console.error('Failed to reset org user data:', error);
        },
    });
};

// Stats and analytics hooks
export const useUserStats = (orgId: string) => {
    const orgData = useOrgUserData(orgId);

    return useMemo(() => {
        const bookmarksCount = orgData.bookmarks.length;
        const lastViewedCount = orgData.lastViewed.length;

        return {
            bookmarksCount,
            lastViewedCount,
        };
    }, [orgData]);
};

// Cross-organization hooks
export const useAllOrgBookmarks = () => {
    const { data: userData } = useUserData();

    return useMemo(() => {
        const allBookmarks = [];
        for (const [orgId, orgData] of Object.entries(userData)) {
            for (const videoId of orgData.bookmarks) {
                allBookmarks.push({ orgId, videoId });
            }
        }
        return allBookmarks;
    }, [userData]);
};

export const useAllOrgLastViewed = () => {
    const { data: userData } = useUserData();

    return useMemo(() => {
        const allLastViewed = [];
        for (const [orgId, orgData] of Object.entries(userData)) {
            for (const video of orgData.lastViewed) {
                allLastViewed.push({ orgId, ...video });
            }
        }
        // Sort by viewedAt descending
        return allLastViewed.sort((a, b) => new Date(b.viewedAt).getTime() - new Date(a.viewedAt).getTime());
    }, [userData]);
};
